/**
 * Carrier MCP stdio — the browser-agent runtime for a local CLI process.
 *
 * WHAT THIS REPLACES
 * ------------------
 * Until now the stdio package carried five `manus-*` modules (2,120 lines) and
 * three tool files wired to them, and not one line of it could ever run: the env
 * shim in `index.ts` set `MANUS_API_KEY` to the empty string with no
 * `process.env` fallback, unlike every other configurable secret beside it, and
 * every handler guarded on that key. Every `ui_agent_*` call returned a
 * configuration error, for everyone, always. Those modules are deleted; the
 * client now comes from `@carrier/ai`, shared with the Worker rather than
 * copied a fifth time.
 *
 * WHY A MAP AND NOT A FILE
 * ------------------------
 * `@carrier/ai` takes an injected `UiAgentStore` instead of a KV binding. The
 * Worker passes `CARRIER_USERS`; stdio has no KV, so it passes the Map below.
 *
 * The consequence is deliberate and needs stating: task records live only for
 * the lifetime of this process. A `ui_agent_ask` dispatch and the
 * `ui_agent_status` poll that reads it must happen in the same MCP session,
 * which they do — an MCP client holds one stdio process open for the session.
 * Persisting to disk was rejected: the records carry the agent's prompt, its
 * page content and its session viewer URL, and writing that to a file in a
 * user's home directory is a durable disclosure to buy a guarantee no caller
 * actually needs.
 *
 * TTLs are honoured rather than ignored. Workers KV expires entries on its own;
 * a Map does not, so a long-lived session would otherwise accumulate every task
 * it ever ran, prompt text included, until the process exits.
 */

import type { UiAgentAuditSink, UiAgentEnv, UiAgentStore } from "@carrier/ai";

// ---------------------------------------------------------------------------
// Map-backed store
// ---------------------------------------------------------------------------

interface Entry {
  value: string;
  /** Epoch ms, or null when the caller set no TTL. */
  expiresAt: number | null;
}

/**
 * A `UiAgentStore` over an in-process Map, honouring `expirationTtl`.
 *
 * Expiry is checked on read rather than swept on a timer: a timer would keep
 * the Node event loop alive and stop a CLI process from exiting on its own.
 */
export function createMemoryStore(): UiAgentStore {
  const map = new Map<string, Entry>();

  return {
    async get(key) {
      const hit = map.get(key);
      if (!hit) return null;
      if (hit.expiresAt !== null && Date.now() >= hit.expiresAt) {
        map.delete(key);
        return null;
      }
      return hit.value;
    },
    async put(key, value, options) {
      const ttl = options?.expirationTtl;
      map.set(key, {
        value,
        expiresAt: ttl === undefined ? null : Date.now() + ttl * 1000,
      });
    },
    async delete(key) {
      map.delete(key);
    },
  };
}

// ---------------------------------------------------------------------------
// Spend guard
// ---------------------------------------------------------------------------

/**
 * Default ceiling on hosted runs per CLI process.
 *
 * A run costs roughly $0.20-1.00 of real money. The Worker bounds this with
 * `reserveAiCalls` against KV; stdio has no KV and therefore none of that, so
 * without a ceiling here an agent in a retry loop could spend without limit
 * against a key the user supplied.
 *
 * Ten is set to be generous for real work and small enough that a runaway is
 * capped at single-digit dollars rather than open-ended.
 */
export const DEFAULT_MAX_RUNS_PER_PROCESS = 10;

/** Env var name, so the error message and the reader cannot drift apart. */
export const MAX_RUNS_ENV = "CARRIER_BROWSER_USE_MAX_RUNS";

export class SpendLimitError extends Error {
  constructor(readonly limit: number) {
    super(
      `Browser-agent run limit reached: ${limit} run(s) in this CLI session. ` +
        `Each run costs roughly $0.20-1.00 against your own BROWSER_USE_API_KEY. ` +
        `Raise it with ${MAX_RUNS_ENV}=<n>, or 0 to disable the browser agent entirely. ` +
        `Restarting the CLI resets the count.`,
    );
    this.name = "SpendLimitError";
  }
}

/**
 * Read the per-process ceiling.
 *
 * `0` is a meaningful value — it turns the capability off while leaving the
 * tools registered — so it must not be collapsed into "unset" by a `||`. An
 * unparseable or negative value falls back to the default rather than throwing:
 * a typo in an env var should not make the whole CLI unusable, and the default
 * is the safe direction to fail in.
 */
export function maxRunsPerProcess(
  env: NodeJS.ProcessEnv = process.env,
): number {
  const raw = env[MAX_RUNS_ENV];
  if (raw === undefined || raw.trim() === "") {
    return DEFAULT_MAX_RUNS_PER_PROCESS;
  }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return DEFAULT_MAX_RUNS_PER_PROCESS;
  }
  return parsed;
}

/**
 * Counts dispatches for this process and refuses past the ceiling.
 *
 * WHAT THIS PREVENTS, AND WHAT IT DOES NOT
 * ----------------------------------------
 * It prevents the realistic failure: an agent loop, a mistyped instruction or a
 * retry storm inside one MCP session burning an unbounded amount.
 *
 * It does NOT prevent a caller that respawns the CLI between runs — each new
 * process gets a fresh count, because there is nowhere durable to keep one
 * without writing state to the user's disk. It also cannot cap dollars, only
 * runs: the vendor bills the run, and the cost is only known after it finishes.
 * A user who wants a hard financial ceiling should set a spend limit in their
 * Browser Use account, which is the only place that can actually enforce one.
 *
 * The money is the user's own, since they supply the key. This guard exists so
 * a mistake costs them a few dollars rather than an open-ended amount.
 */
export class RunBudget {
  private used = 0;

  constructor(private readonly limit: number) {}

  /** Consume one run, or throw. Call immediately before dispatching. */
  reserve(): void {
    if (this.used >= this.limit) throw new SpendLimitError(this.limit);
    this.used += 1;
  }

  get remaining(): number {
    return Math.max(0, this.limit - this.used);
  }

  get spent(): number {
    return this.used;
  }
}

// ---------------------------------------------------------------------------
// Env assembly
// ---------------------------------------------------------------------------

/** Records what a run actually cost, for `ui_agent_usage` to report back. */
export interface SessionSpend {
  runs: number;
  costUsd: number;
}

export interface StdioUiAgentRuntime {
  env: UiAgentEnv;
  budget: RunBudget;
  spend: SessionSpend;
  configured: boolean;
  /**
   * Task ids dispatched by this process.
   *
   * `UiAgentStore` has no `list()`, deliberately: it is the three methods the
   * shared module needs, and adding a prefix scan to serve one caller would push
   * a KV-shaped concern into every future store. `ui_agent_list_pending` needs
   * to enumerate somehow, so the dispatcher records what it dispatched. In a
   * single-operator CLI that set is exactly the tasks that could ever be
   * pending.
   */
  dispatchedTaskIds: Set<string>;
}

/**
 * Build the browser-agent runtime for this process.
 *
 * `BROWSER_USE_API_KEY` is read from `process.env` with a real fallback, the
 * way `STRIPE_SECRET_KEY` and `ATLAS_BASE_URL` already are in the same env
 * shim. Absent is a valid state: the tools stay registered (catalog parity with
 * the Worker) and return an explicit configuration error, rather than
 * disappearing from `tools/list`.
 *
 * The audit sink accumulates real vendor-billed spend from the row
 * `@carrier/ai` emits — doubles[1] is `costUsd`, appended past the pre-existing
 * length double. stdio stubs `AUDIT_LOG` to a no-op, so this is the only place
 * the per-run figure survives locally, and it is what `ui_agent_usage` reports.
 */
export function createUiAgentRuntime(
  processEnv: NodeJS.ProcessEnv = process.env,
): StdioUiAgentRuntime {
  const apiKey = processEnv.BROWSER_USE_API_KEY;
  const spend: SessionSpend = { runs: 0, costUsd: 0 };

  const audit: UiAgentAuditSink = (row) => {
    const cost = row.doubles?.[1];
    if (typeof cost === "number" && cost > 0) {
      spend.runs += 1;
      spend.costUsd = Math.round((spend.costUsd + cost) * 1e6) / 1e6;
    }
  };

  return {
    env: {
      ...(apiKey ? { apiKey } : {}),
      store: createMemoryStore(),
      audit,
    },
    budget: new RunBudget(maxRunsPerProcess(processEnv)),
    spend,
    configured: Boolean(apiKey),
    dispatchedTaskIds: new Set<string>(),
  };
}
